import { useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

export const fmtH = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const h = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString()
    ? h : `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${h}`;
};

export const Ticks = ({ r }) => (
  r.statut === 'envoyee'
    ? <span className="text-white/40 text-xs flex-shrink-0" title="Envoyée">✓</span>
    : <span className="text-sky-400 text-xs flex-shrink-0" title={r.statut === 'repondue' ? 'Répondue' : 'Vue par le destinataire'}>✓✓</span>
);

const Reponse = ({ r }) => r.reponse ? (
  <div className="mt-2 text-xs text-emerald-300/95 bg-emerald-500/10 border border-emerald-400/20 rounded-xl px-3 py-2 leading-relaxed">
    ↳ {r.reponse} <span className="text-[10px] text-white/35 ml-1">{fmtH(r.repondu_at)}</span>
  </div>
) : null;

const CarteTraitee = ({ r, envoyee, onArchive, archived }) => (
  <div className={`rounded-2xl p-3.5 mb-2.5 bg-white/5 border border-white/10 ${archived ? 'opacity-60' : ''}`}
    data-testid={`agent-req-${archived ? 'archivee' : envoyee ? 'envoyee' : 'recue-repondue'}-${r.id}`}>
    <div className="flex items-center gap-2">
      <span className="text-xs font-bold text-white/60">{envoyee ? `→ ${r.a_nom}` : r.de_nom}</span>
      <span className="ml-auto text-[10px] text-white/40 flex-shrink-0">{fmtH(r.created_at)}</span>
      {envoyee && <Ticks r={r} />}
    </div>
    <div className="text-sm text-white/90 leading-relaxed mt-1">{r.objet}</div>
    <Reponse r={r} />
    {!archived && r.statut === 'repondue' && (
      <button data-testid={`agent-req-archive-${r.id}`} onClick={() => onArchive(r.id)}
        className="mt-2.5 w-full min-h-[40px] rounded-full border border-white/15 text-white/70 hover:text-white hover:bg-white/10 text-[13px] font-bold">
        🗂️ Archiver
      </button>
    )}
  </div>
);

export const AgentRequestsPanel = ({ reqs, refresh, onExec, userEmail }) => {
  const [drafts, setDrafts] = useState({});
  const [showArchived, setShowArchived] = useState(false);

  if (!reqs) return <div className="p-6 text-sm text-white/40">Chargement…</div>;

  const aTraiter = reqs.recues.filter((r) => r.statut !== 'repondue');
  const traitees = reqs.recues.filter((r) => r.statut === 'repondue');
  const vide = !aTraiter.length && !traitees.length && !reqs.envoyees.length && !reqs.archivees.length;

  const archiver = async (id) => {
    try {
      await api.post(`/agent-os/requests/${id}/archive`);
      toast.success('Demande archivée 🗂️');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const repondre = async (id, texte, deNom) => {
    const rep = (texte || '').trim();
    if (!rep) return;
    try {
      await api.post(`/agent-os/requests/${id}/reply`, { reponse: rep });
      toast.success(`Réponse envoyée à ${deNom} 📨`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 min-h-[200px]" data-testid="agent-requests-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wide text-white/40 font-bold">
          {showArchived ? `🗂️ Archivées (${reqs.archivees.length})` : `📨 Demandes${reqs.en_attente > 0 ? ` · ${reqs.en_attente} à traiter` : ''}`}
        </div>
        {reqs.archivees.length > 0 && (
          <button data-testid="agent-req-archived-toggle" onClick={() => setShowArchived(!showArchived)}
            className="text-xs font-bold text-white/50 hover:text-white px-3 py-1.5 rounded-full border border-white/15 hover:bg-white/10">
            {showArchived ? '← Retour' : `🗂️ Archivées (${reqs.archivees.length})`}
          </button>
        )}
      </div>

      {vide && <div className="text-sm text-white/50 mt-4">Aucune demande — tout est à jour ✅</div>}

      {showArchived ? (
        reqs.archivees.map((r) => (
          <CarteTraitee key={r.id} r={r} envoyee={r.de_email === userEmail} onArchive={archiver} archived />
        ))
      ) : (<>
        {aTraiter.map((r) => (
          <div key={r.id} className={`rounded-2xl p-3.5 mb-3 border ${r.urgence === 'urgente' ? 'bg-rose-500/15 border-rose-400/40' : 'bg-orange-500/10 border-orange-400/30'}`}
            data-testid={`agent-req-recue-${r.id}`}>
            <div className="flex items-center gap-2 mb-1">
              {r.urgence === 'urgente' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-600 text-white">🚨 URGENT</span>}
              <span className="font-bold text-orange-300 text-sm">{r.de_nom}</span>
              <span className="ml-auto text-[10px] text-white/40">{fmtH(r.created_at)}</span>
            </div>
            <div className="text-sm text-white/95 leading-relaxed">{r.objet}</div>
            {r.doublon_de && (
              <div className="mt-2.5 rounded-xl bg-amber-500/15 border border-amber-400/40 p-3 text-xs text-amber-200"
                data-testid={`agent-req-doublon-${r.id}`}>
                ⚠️ <b>Doublon possible</b> — demande similaire {r.doublon_de.statut === 'repondue'
                  ? `déjà traitée (${fmtH(r.doublon_de.repondu_at)})` : 'déjà en attente'} : « {r.doublon_de.objet} »
                {r.doublon_de.reponse && <div className="mt-1 text-amber-100/90">↳ {r.doublon_de.reponse}</div>}
                {r.doublon_de.reponse && (
                  <button data-testid={`agent-req-deja-fait-${r.id}`}
                    onClick={() => repondre(r.id, `✅ Déjà fait — ${r.doublon_de.reponse}`, r.de_nom)}
                    className="mt-2 w-full min-h-[38px] rounded-full bg-amber-500/25 hover:bg-amber-500/40 text-amber-100 text-xs font-bold border border-amber-400/40">
                    ↩️ Répondre « Déjà fait »
                  </button>
                )}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <input value={drafts[r.id] || ''} placeholder="Ta réponse…" data-testid={`agent-req-reply-input-${r.id}`}
                onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && repondre(r.id, drafts[r.id], r.de_nom)}
                className="flex-1 bg-white/10 border border-white/15 rounded-full px-3.5 min-h-[42px] text-sm placeholder:text-white/30" />
              <button data-testid={`agent-req-reply-btn-${r.id}`}
                onClick={() => repondre(r.id, drafts[r.id], r.de_nom)}
                className="px-4 min-h-[42px] rounded-full bg-orange-500 hover:bg-orange-400 text-white text-[13px] font-bold">Répondre</button>
            </div>
            <button data-testid={`agent-req-exec-btn-${r.id}`} onClick={() => onExec(r)}
              className="mt-2 w-full min-h-[42px] rounded-full border border-orange-400/40 text-orange-300 hover:text-orange-200 hover:bg-orange-500/10 text-[13px] font-bold">
              ⚡ Confier à l'agent — il propose, tu valides, il répond à {r.de_nom}
            </button>
          </div>
        ))}

        {traitees.length > 0 && (
          <div className="text-[11px] uppercase tracking-wide text-white/35 font-bold mt-4 mb-2">Reçues · traitées</div>
        )}
        {traitees.map((r) => <CarteTraitee key={r.id} r={r} envoyee={false} onArchive={archiver} />)}

        {reqs.envoyees.length > 0 && (
          <div className="text-[11px] uppercase tracking-wide text-white/35 font-bold mt-4 mb-2">Envoyées</div>
        )}
        {reqs.envoyees.map((r) => <CarteTraitee key={r.id} r={r} envoyee onArchive={archiver} />)}
      </>)}
    </div>
  );
};
